import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { Plus, Trash } from "lucide-react"
import { useEffect, useState } from "react"
import { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { WidgetConfigurationFormType } from ".."

type BubbleMessagesProps = {
  form: UseFormReturn<WidgetConfigurationFormType>
}

export const BubbleMessages = ({ form }: BubbleMessagesProps) => {
  const { t } = useTranslation()
  const languages = form.watch("languages")
  const formMessages = form.watch("bubble.messages")
  const [messages, setMessages] = useState<{ locale: string; text: string }[]>([])

  const convertToFormFormat = (currentMessages: typeof messages) => {
    const formatted = currentMessages.reduce<Record<string, string[]>>((a, b) => {
      if (!a[b.locale]) {
        a[b.locale] = []
      }

      a[b.locale]?.push(b.text)

      return a
    }, {})

    form.setValue("bubble.messages", formatted)
    form.trigger()
  }

  const onAddNewMessage = () => {
    const currentMessages = [...messages]
    currentMessages.push({ locale: "en", text: "" })

    setMessages(currentMessages)
  }

  const onValueChange = (index: number, key: "text" | "locale", value: string) => {
    const currentMessages = [...messages]

    if (currentMessages?.[index]) {
      currentMessages[index][key] = value
    }

    setMessages(currentMessages)
    convertToFormFormat(currentMessages)
  }

  const onDeleteOption = (index: number) => {
    const currentMessages = [...messages]
    currentMessages.splice(index, 1)

    setMessages(currentMessages)
    convertToFormFormat(currentMessages)
  }

  useEffect(() => {
    const messages = Object.entries(formMessages ?? {})
      .map(
        ([locale, texts]) =>
          texts?.map((text) => ({
            locale,
            text,
          })) ?? [],
      )
      .flat()

    setMessages(messages)
  }, [formMessages])

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-4 gap-4">
        <FormField
          control={form.control}
          name="bubble.displayDelay"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("campaigns.templates.widget.config.bubbleMessages.displayConfig.displayAfter")}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={"1500"}
                  type="number"
                  {...form.register(field.name, {
                    valueAsNumber: true,
                    validate: (value) => (value ? value > 0 : true),
                  })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="bubble.removeAfter"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("campaigns.templates.widget.config.bubbleMessages.displayConfig.removeAfter")}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={"1500"}
                  type="number"
                  {...form.register(field.name, {
                    valueAsNumber: true,
                    validate: (value) => (value ? value > 0 : true),
                  })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormItem className="col-span-2">
          <FormLabel>
            {t("campaigns.templates.widget.config.bubbleMessages.displayConfig.desktopBubbles")}
          </FormLabel>
          <FormControl>
            <div>
              <Checkbox
                checked={form.watch("bubble.removeOnDesktop")}
                id="desktopBubbles"
                className="cursor-pointer"
                onCheckedChange={(value) => form.setValue("bubble.removeOnDesktop", !!value)}
              />
              <Label
                className="font-normal inline ml-1 relative -top-[1px] cursor-pointer"
                htmlFor="desktopBubbles"
              >
                <span>
                  {t(
                    "campaigns.templates.widget.config.bubbleMessages.displayConfig.removeOnDesktop",
                  )}
                </span>
              </Label>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </div>
      <div>
        {messages.length > 0 && (
          <div className="grid gap-2 mb-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
              <div className="col-span-1 lg:col-span-9">
                <Label>{t("campaigns.templates.widget.config.bubbleMessages.message")}</Label>
              </div>
              <div className="col-span-1 lg:col-span-2">
                <Label>{t("campaigns.templates.widget.config.bubbleMessages.language")}</Label>
              </div>
            </div>
            {messages.map((message, index) => (
              <div key={message.text} className="grid grid-cols-1 lg:grid-cols-12 gap-2 group/row">
                <div className="col-span-1 lg:col-span-9">
                  <Input
                    onBlur={(ev) => onValueChange(index, "text", ev.target.value)}
                    defaultValue={message.text}
                  />
                </div>
                <div className="col-span-1 lg:col-span-2">
                  <Select
                    onValueChange={(option) => onValueChange(index, "locale", option)}
                    value={message.locale}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((language) => (
                        <SelectItem key={language.value} value={language.value}>
                          {language.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1 flex justify-end items-center duration-100 opacity-0 group-hover/row:opacity-100">
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={() => onDeleteOption(index)}
                  >
                    <Trash />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <Button
          variant="outline"
          onClick={onAddNewMessage}
          type="button"
          disabled={!!messages.find((e) => !e.text)}
        >
          <Plus />
          <span>{t("campaigns.templates.widget.config.bubbleMessages.addNew")}</span>
        </Button>
      </div>
    </div>
  )
}
